<template>
  <md-dialog
    :md-active.sync="active"
    class="assign-hw-dialog"
    style="
      max-width: 1400px;
      width: 90vw !important;
      left: 50% !important;
      transform: translateX(-50%) !important;
    "
  >
    <md-dialog-title
      style="
        display: flex;
        justify-content: space-between;
        border-bottom: 1px solid #eee;
        padding-bottom: 10px;
      "
    >
      <div class="title">{{ $t('Cập nhật đề thi') || $t('ASSIGN_NEW') }}</div>
      <md-button class="md-icon-button" @click.prevent="closeDialog">
        <md-icon>close</md-icon>
      </md-button>
    </md-dialog-title>

    <md-dialog-content class="dialog-content">
      <div class="split-pane">
        <!-- Left Pane: Homework Settings -->
        <div class="left-pane">
          <div class="input-field">
            <label class="field-label">* Tên đề thi</label>
            <BaseInputQuestionText
              :placeholder="$t('HW_NAME')"
              v-model="hwTemp.name"
            />
          </div>

          <div class="input-field" v-if="config && config.subjects">
            <label class="field-label">* Môn học / Ngành học</label>
            <BaseInputSelectV2
              :options="config.subjects"
              :optValue="`${$i18n.locale}Value`"
              v-model="hwTemp.subject"
            />
          </div>

          <div class="flex-row">
            <div class="input-field half-width">
              <label class="field-label">* Thời gian bắt đầu</label>
              <BaseDatePicker v-model="hwTemp.startAt" />
            </div>
            <div class="input-field half-width">
              <label class="field-label">* Thời gian kết thúc</label>
              <BaseDatePicker v-model="hwTemp.endAt" />
            </div>
          </div>

          <div class="input-field">
            <label class="field-label">Giới hạn số lần nộp</label>
            <BaseInputText
              :max="100"
              type="number"
              v-model="hwTemp.limitSubmit"
            />
          </div>

          <div class="input-field">
            <label class="field-label">Trạng thái</label>
            <div style="display: flex; align-items: center; gap: 10px">
              <span>Chưa xuất bản</span>
              <el-switch
                v-model="hwTemp.publish"
                active-color="#13ce66"
                inactive-color="#ff4949"
              ></el-switch>
              <span style="color: #13ce66" v-if="hwTemp.publish"
                >Đã xuất bản</span
              >
            </div>
          </div>

          <div class="input-field" style="margin-top: 20px">
            <BaseCheckbox
              :label="$t('SHOW_ANSWER_KEY')"
              v-model="hwTemp.showAnswerKey"
            />
          </div>
        </div>

        <!-- Right Pane: Class & Student Assignment -->
        <div class="right-pane">
          <div class="input-field">
            <label class="field-label">Giao cho</label>
            <BaseInputSelectV2
              :multiple="true"
              :filterable="true"
              :options="classes"
              :optValue="'name'"
              v-model="selectedClassIds"
              placeholder="Chọn lớp"
              @update="handleClassesChanged"
            />
          </div>

          <div class="class-list">
            <div v-for="cId in selectedClassIds" :key="cId" class="class-item">
              <div class="class-header">
                <div class="class-name">{{ getClassName(cId) }}</div>
                <div style="display: flex; gap: 8px; align-items: center">
                  <el-button
                    size="small"
                    :type="isAllStudentsSelected(cId) ? 'primary' : 'default'"
                    @click="selectAllStudents(cId)"
                  >
                    <i
                      :class="isAllStudentsSelected(cId) ? 'el-icon-check' : ''"
                    ></i>
                    Cả lớp
                  </el-button>
                  <el-button
                    size="small"
                    @click="toggleExpand(cId)"
                    :class="{ expanded: expandedClasses[cId] }"
                  >
                    <i
                      :class="[
                        'el-icon-arrow-down',
                        { 'icon-rotated': expandedClasses[cId] }
                      ]"
                    ></i>
                  </el-button>
                </div>
              </div>

              <!-- Display all students chips -->
              <div class="selected-students" v-if="expandedClasses[cId]">
                <span
                  class="student-chip all-chip"
                  v-if="isAllStudentsSelected(cId)"
                  @click="selectAllStudents(cId)"
                  >Cả lớp</span
                >
                <div
                  class="student-chip"
                  v-for="student in studentsByClass[cId] || []"
                  :key="student.eID"
                  @click="
                    handleStudentCheckboxChange(
                      cId,
                      student.eID,
                      !(assignsMap[cId] || []).includes(student.eID)
                    )
                  "
                  :class="{
                    selected: (assignsMap[cId] || []).includes(student.eID)
                  }"
                >
                  <img
                    v-if="student.avatar"
                    :src="student.avatar | media"
                    class="chip-avatar"
                  />
                  <span class="chip-name">{{
                    student.displayName || student.fullname
                  }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="dialog-actions">
        <md-button class="md-raised" @click.prevent="closeDialog">
          {{ $t('CANCEL') }}
        </md-button>
        <md-button
          class="md-raised md-primary text-white"
          @click.prevent="submit"
          :disabled="isSubmitting"
        >
          {{ $t('Cập nhật') || $t('UPDATE') }}
        </md-button>
      </div>
    </md-dialog-content>
  </md-dialog>
</template>

<script>
import { mapState } from 'vuex'
import { getStudents } from '@/api/content'

export default {
  name: 'AssignHomeworkDialog',
  props: {
    active: {
      type: Boolean,
      default: false
    },
    exam: {
      type: Object,
      default: () => null
    }
  },
  data() {
    return {
      isSubmitting: false,
      hwTemp: {
        name: '',
        subject: null,
        startAt: '',
        endAt: '',
        limitSubmit: -1,
        publish: false,
        showAnswerKey: false
      },
      selectedClassIds: [],
      studentsByClass: {}, // classId -> [students]
      assignsMap: {}, // classId -> [eID1, eID2, ...]
      expandedClasses: {} // classId -> boolean
    }
  },
  computed: {
    ...mapState('content', ['classes']),
    ...mapState('exam-vault', ['config'])
  },
  watch: {
    active(val) {
      if (val && this.exam) {
        this.resetForm()
      }
    }
  },
  methods: {
    resetForm() {
      this.hwTemp = {
        examVaultId: this.exam._id,
        name: this.exam.title || '',
        subject: this.exam.subject?._id || this.exam.subject,
        startAt: new Date(),
        endAt: new Date(new Date().getTime() + 7 * 24 * 60 * 60 * 1000), // +7 days
        limitSubmit: -1,
        publish: false,
        showAnswerKey: false
      }
      this.selectedClassIds = []
      this.studentsByClass = {}
      this.assignsMap = {}
      this.expandedClasses = {}
    },
    closeDialog() {
      this.$emit('update:active', false)
    },
    getClassName(classId) {
      const cls = this.classes.find((c) => c._id === classId)
      return cls ? cls.name : 'Unknown Class'
    },
    getStudentName(classId, eID) {
      const students = this.studentsByClass[classId] || []
      const student = students.find((s) => s.eID === eID)
      return student ? student.fullname : eID
    },
    getStudentDisplayName(classId, eID) {
      const students = this.studentsByClass[classId] || []
      const student = students.find((s) => s.eID === eID)
      return student ? student.displayName || student.fullname : eID
    },
    getStudentAvatar(classId, eID) {
      const students = this.studentsByClass[classId] || []
      const student = students.find((s) => s.eID === eID)
      return student ? student.avatar : null
    },
    async handleClassesChanged(newClassIds) {
      // Find newly added classes and fetch their students
      for (const classId of newClassIds) {
        if (!this.studentsByClass[classId]) {
          try {
            const res = await getStudents({ classId })
            if (res && res.data && res.data.students) {
              const students = res.data.students.filter(
                (d) => d.eID && d.status === 'approved'
              )
              this.$set(this.studentsByClass, classId, students)
              // By default, select all students
              this.$set(
                this.assignsMap,
                classId,
                students.map((s) => s.eID)
              )
            }
          } catch (error) {
            console.error('Failed to fetch students for class', classId, error)
            this.$set(this.studentsByClass, classId, [])
            this.$set(this.assignsMap, classId, [])
          }
        }
      }
      // Remove classes that were unselected
      for (const classId in this.assignsMap) {
        if (!newClassIds.includes(classId)) {
          this.$delete(this.assignsMap, classId)
          this.$delete(this.expandedClasses, classId)
        }
      }
    },
    toggleExpand(classId) {
      this.$set(this.expandedClasses, classId, !this.expandedClasses[classId])
    },
    handleStudentCheckboxChange(classId, eID, checked) {
      const current = this.assignsMap[classId] || []
      if (checked) {
        if (!current.includes(eID)) {
          this.$set(this.assignsMap, classId, [...current, eID])
        }
      } else {
        this.$set(
          this.assignsMap,
          classId,
          current.filter((id) => id !== eID)
        )
      }
    },
    selectAllStudents(classId) {
      const students = this.studentsByClass[classId] || []
      const current = this.assignsMap[classId] || []
      const isAllSelected = current.length === students.length

      if (isAllSelected) {
        // Deselect all
        this.$set(this.assignsMap, classId, [])
      } else {
        // Select all
        this.$set(
          this.assignsMap,
          classId,
          students.map((s) => s.eID)
        )
      }
    },
    isAllStudentsSelected(classId) {
      const current = this.assignsMap[classId] || []
      const students = this.studentsByClass[classId] || []
      return students.length > 0 && current.length === students.length
    },
    getAssignedEIDs(classId) {
      return this.assignsMap[classId] || []
    },
    getMappedSubject(subjectId) {
      if (!subjectId) return null

      const subjectMap = {
        english: 'en',
        math: 'math',
        vietnamese: 'vi',
        chemistry: 'chem',
        chinese: 'zh',
        vi: 'vi',
        en: 'en'
      }

      if (this.config && this.config.subjects) {
        const found = this.config.subjects.find((s) => s._id === subjectId)
        if (found && found.value) {
          const valLower = found.value.toLowerCase()
          return subjectMap[valLower] || valLower.substring(0, 2)
        }
      }

      // Fallback if it's already string like 'english' or 'en'
      const idLower = subjectId.toLowerCase()
      return subjectMap[idLower] || idLower.substring(0, 2)
    },
    async submit() {
      if (this.selectedClassIds.length === 0) {
        this.$notify({
          type: 'error',
          title: 'Lỗi',
          message: 'Vui lòng chọn ít nhất 1 lớp để giao bài'
        })
        return
      }

      const assigns = this.selectedClassIds.map((classId) => {
        return {
          classId: classId,
          assignedEIDs: this.getAssignedEIDs(classId)
        }
      })

      const payload = {
        ...this.hwTemp,
        subject: this.getMappedSubject(this.hwTemp.subject),
        assigns
      }

      this.isSubmitting = true
      try {
        await this.$store.dispatch('exam-vault/createHomework', payload)
        this.$emit('success')
        this.closeDialog()
      } catch (error) {
        this.$notify({
          type: 'error',
          title: 'Lỗi',
          message: error.message || 'Không thể giao bài tập'
        })
      } finally {
        this.isSubmitting = false
      }
    }
  }
}
</script>

<style>
.md-dialog.assign-hw-dialog {
  max-width: 1400px !important;
  width: 90vw !important;
  left: 50% !important;
  transform: translateX(-50%) !important;
}

.md-dialog-container {
  max-width: 100% !important;
}
</style>

<style scoped lang="scss">
.dialog-content {
  padding: 0 !important;
}

.split-pane {
  display: flex;
  min-height: 500px;

  .left-pane {
    flex: 1;
    padding: 24px;
    border-right: 1px solid #eee;
  }

  .right-pane {
    flex: 1;
    padding: 24px;
    background-color: #fafafa;
  }
}

.field-label {
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
  display: block;
}

.input-field {
  margin-bottom: 20px;
}

.flex-row {
  display: flex;
  gap: 16px;

  .half-width {
    flex: 1;
  }
}

.class-list {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.class-item {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;

  .class-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;

    .class-name {
      font-weight: 500;
      color: #333;
      flex: 1;
    }
  }

  .selected-students {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;

    .student-chip {
      display: flex;
      align-items: center;
      gap: 6px;
      background: #f5f5f5;
      color: #333;
      font-size: 12px;
      padding: 4px 10px;
      border-radius: 16px;
      border: 1.5px solid transparent;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        background: #efefef;
      }

      &.selected {
        background: #e3f2fd;
        color: #1976d2;
        border-color: #1976d2;

        &:hover {
          background: #bbdefb;
          box-shadow: 0 2px 4px rgba(25, 118, 210, 0.2);
        }
      }

      .chip-avatar {
        width: 20px;
        height: 20px;
        border-radius: 50%;
        object-fit: cover;
      }

      .chip-name {
        flex-shrink: 0;
      }

      &.all-chip {
        background: #e8f5e9;
        color: #2e7d32;
        border-color: #2e7d32;

        &:hover {
          background: #c8e6c9;
        }
      }
    }
  }
}

.dialog-actions {
  padding: 16px 24px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.el-button.expanded {
  i {
    transform: rotate(180deg);
    transition: transform 0.3s ease;
  }
}

.icon-rotated {
  display: inline-block;
  transform: rotate(180deg);
  transition: transform 0.3s ease;
}
</style>
